import { useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Seo from './components/Seo';
import ScrollToTop from './components/ScrollToTop';
import ConsentBanner from './components/ConsentBanner';
import PageTracker from './components/PageTracker';
import { AuthProvider } from './portal/context/AuthContext';
import './App.css';

// Lazy load components below the fold
const Services = lazy(() => import('./components/Services'));
const Pricing = lazy(() => import('./components/Pricing'));
const Contact = lazy(() => import('./components/Contact'));
const Portfolio = lazy(() => import('./components/Portfolio'));
const About = lazy(() => import('./components/About'));
const Footer = lazy(() => import('./components/Footer'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const Terms = lazy(() => import('./components/Terms'));
const CookiePolicy = lazy(() => import('./components/CookiePolicy'));
const WebbdesignPage = lazy(() => import('./components/ServicePage').then((m) => ({ default: m.WebbdesignPage })));
const EhandelPage = lazy(() => import('./components/ServicePage').then((m) => ({ default: m.EhandelPage })));
const SeoPage = lazy(() => import('./components/ServicePage').then((m) => ({ default: m.SeoPage })));
const HemsidaFalunPage = lazy(() => import('./components/ServicePage').then((m) => ({ default: m.HemsidaFalunPage })));
const SystemPage = lazy(() => import('./components/ServicePage').then((m) => ({ default: m.SystemPage })));
const PriceCalculator = lazy(() => import('./components/PriceCalculator'));
const BlogIndex = lazy(() => import('./components/Blog').then((m) => ({ default: m.BlogIndex })));
const BlogPost = lazy(() => import('./components/Blog').then((m) => ({ default: m.BlogPost })));
const NotFound = lazy(() => import('./components/NotFound'));

// Portal pages (lazy loaded)
const LoginPage = lazy(() => import('./portal/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./portal/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./portal/pages/ResetPasswordPage'));
const RequireAuth = lazy(() => import('./portal/components/RequireAuth'));
const RequireAdmin = lazy(() => import('./portal/components/RequireAdmin'));
const PortalLayout = lazy(() => import('./portal/components/PortalLayout'));
const DashboardHome = lazy(() => import('./portal/pages/DashboardHome'));
const ChangeRequestList = lazy(() => import('./portal/pages/ChangeRequestList'));
const ChangeRequestNew = lazy(() => import('./portal/pages/ChangeRequestNew'));
const ChangeRequestDetail = lazy(() => import('./portal/pages/ChangeRequestDetail'));
const InvoiceList = lazy(() => import('./portal/pages/InvoiceList'));
const InvoiceDetail = lazy(() => import('./portal/pages/InvoiceDetail'));
const AgreementList = lazy(() => import('./portal/pages/AgreementList'));
const AgreementDetail = lazy(() => import('./portal/pages/AgreementDetail'));
const SubscriptionList = lazy(() => import('./portal/pages/SubscriptionList'));
const FileManager = lazy(() => import('./portal/pages/FileManager'));
const ProfileEdit = lazy(() => import('./portal/pages/ProfileEdit'));

// Admin pages (lazy loaded)
const AdminLayout = lazy(() => import('./portal/components/AdminLayout'));
const AdminDashboard = lazy(() => import('./portal/admin/AdminDashboard'));
const AdminUserList = lazy(() => import('./portal/admin/AdminUserList'));
const AdminUserCreate = lazy(() => import('./portal/admin/AdminUserCreate'));
const AdminUserEdit = lazy(() => import('./portal/admin/AdminUserEdit'));
const AdminChangeRequestList = lazy(() => import('./portal/admin/AdminChangeRequestList'));
const AdminChangeRequestDetail = lazy(() => import('./portal/admin/AdminChangeRequestDetail'));
const AdminInvoiceList = lazy(() => import('./portal/admin/AdminInvoiceList'));
const AdminInvoiceCreate = lazy(() => import('./portal/admin/AdminInvoiceCreate'));
const AdminInvoiceEdit = lazy(() => import('./portal/admin/AdminInvoiceEdit'));
const AdminSubscriptionList = lazy(() => import('./portal/admin/AdminSubscriptionList'));
const AdminSubscriptionCreate = lazy(() => import('./portal/admin/AdminSubscriptionCreate'));
const AdminSubscriptionEdit = lazy(() => import('./portal/admin/AdminSubscriptionEdit'));
const AdminAgreementList = lazy(() => import('./portal/admin/AdminAgreementList'));
const AdminAgreementDetail = lazy(() => import('./portal/admin/AdminAgreementDetail'));
const AdminFileList = lazy(() => import('./portal/admin/AdminFileList'));
const AdminOrganizationList = lazy(() => import('./portal/admin/AdminOrganizationList'));
const AdminOrganizationCreate = lazy(() => import('./portal/admin/AdminOrganizationCreate'));
const AdminOrganizationEdit = lazy(() => import('./portal/admin/AdminOrganizationEdit'));
const AdminAdminList = lazy(() => import('./portal/admin/AdminAdminList'));
const AdminAdminEdit = lazy(() => import('./portal/admin/AdminAdminEdit'));
const AdminLeadList = lazy(() => import('./portal/admin/AdminLeadList'));
const AdminAnalytics = lazy(() => import('./portal/admin/AdminAnalytics'));

// Loading fallback
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
    color: 'var(--color-text-muted)'
  }}>
    Laddar...
  </div>
);

// Placeholder for lazy loaded sections
const SectionPlaceholder = ({ height = '600px' }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height,
    gap: '1rem'
  }}>
    <div style={{
      width: '48px',
      height: '48px',
      border: '3px solid var(--color-border)',
      borderTopColor: 'var(--color-accent)',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <span style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>Laddar...</span>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// Public layout wrapper with Navbar + Footer
function PublicLayout() {
  return (
    <div className="app-main">
      {/* Först i DOM-ordningen så att tangentbordsanvändare når valet direkt */}
      <ConsentBanner />
      <Navbar />
      <main>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </main>
      <Suspense fallback={<div />}>
        <Footer />
      </Suspense>
    </div>
  );
}

function MainContent() {
  const [selectedPlan, setSelectedPlan] = useState('Vet inte än – börja med gratis prototyp');

  return (
    <>
      <Seo
        title="Briljans | Webbyrå i Falun & Dalarna"
        description="Gratis prototyp – betala först när du är nöjd. Webbyrå i Falun som bygger moderna hemsidor från 5 000 kr, med SEO och e-handel för företag i Dalarna."
        path="/"
      />
      <Hero />
      {/* Tall fallback keeps the footer out of the viewport while the
          lazy sections load, preventing a large layout shift. */}
      <Suspense fallback={<div style={{ minHeight: '200vh' }} />}>
        <Portfolio />
        <Services />
        <About />
        <Pricing onSelectPlan={setSelectedPlan} />
        <Contact selectedPlan={selectedPlan} />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        {/* På appnivå så att även portalbesök uppdaterar senaste sökväg */}
        <PageTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public site - with Navbar + Footer */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<MainContent />} />
              <Route path="/webbdesign" element={<WebbdesignPage />} />
              <Route path="/e-handel" element={<EhandelPage />} />
              <Route path="/seo" element={<SeoPage />} />
              <Route path="/hemsida-falun" element={<HemsidaFalunPage />} />
              <Route path="/skraddarsydda-system" element={<SystemPage />} />
              <Route path="/priskalkylator" element={<PriceCalculator />} />
              <Route path="/blogg" element={<BlogIndex />} />
              <Route path="/blogg/:slug" element={<BlogPost />} />
              <Route path="/integritetspolicy" element={<PrivacyPolicy />} />
              <Route path="/villkor" element={<Terms />} />
              <Route path="/cookies" element={<CookiePolicy />} />
            </Route>

            {/* Auth pages - no Navbar/Footer */}
            <Route path="/portal/logga-in" element={<LoginPage />} />
            <Route path="/portal/glomt-losenord" element={<ForgotPasswordPage />} />
            <Route path="/portal/aterstall-losenord" element={<ResetPasswordPage />} />

            {/* Customer portal */}
            <Route path="/portal" element={<RequireAuth><PortalLayout /></RequireAuth>}>
              <Route index element={<DashboardHome />} />
              <Route path="andringar" element={<ChangeRequestList />} />
              <Route path="andringar/ny" element={<ChangeRequestNew />} />
              <Route path="andringar/:id" element={<ChangeRequestDetail />} />
              <Route path="fakturor" element={<InvoiceList />} />
              <Route path="fakturor/:id" element={<InvoiceDetail />} />
              <Route path="avtal" element={<AgreementList />} />
              <Route path="avtal/:id" element={<AgreementDetail />} />
              <Route path="prenumerationer" element={<SubscriptionList />} />
              <Route path="filer" element={<FileManager />} />
              <Route path="profil" element={<ProfileEdit />} />
            </Route>

            {/* Admin panel */}
            <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
              <Route index element={<AdminDashboard />} />
              <Route path="kunder" element={<AdminUserList />} />
              <Route path="kunder/ny" element={<AdminUserCreate />} />
              <Route path="kunder/:id" element={<AdminUserEdit />} />
              <Route path="organisationer" element={<AdminOrganizationList />} />
              <Route path="organisationer/ny" element={<AdminOrganizationCreate />} />
              <Route path="organisationer/:id" element={<AdminOrganizationEdit />} />
              <Route path="andringar" element={<AdminChangeRequestList />} />
              <Route path="andringar/:id" element={<AdminChangeRequestDetail />} />
              <Route path="fakturor" element={<AdminInvoiceList />} />
              <Route path="fakturor/ny" element={<AdminInvoiceCreate />} />
              <Route path="fakturor/:id" element={<AdminInvoiceEdit />} />
              <Route path="avtal" element={<AdminAgreementList />} />
              <Route path="avtal/:id" element={<AdminAgreementDetail />} />
              <Route path="prenumerationer" element={<AdminSubscriptionList />} />
              <Route path="prenumerationer/ny" element={<AdminSubscriptionCreate />} />
              <Route path="prenumerationer/:id" element={<AdminSubscriptionEdit />} />
              <Route path="filer" element={<AdminFileList />} />
              <Route path="leads" element={<AdminLeadList />} />
              <Route path="statistik" element={<AdminAnalytics />} />
              <Route path="admins" element={<AdminAdminList />} />
              <Route path="admins/:id" element={<AdminAdminEdit />} />
            </Route>

            {/* Catch-all: real 404 instead of a soft redirect */}
            <Route element={<PublicLayout />}>
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
